import { CommonEventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/common/commonEventsEnum';
import { setUserProperties } from 'firebase/analytics';
import Cookies from 'js-cookie';
import { type ReactNode } from 'react';

import { useExperiment } from '@/features/experiment/contexts/Experiment';
import { useSingleTrigger } from '@/hooks/single-trigger';
import { logHubxEvent } from '@/lib/analytics/services/log-hubx-event';
import { getCfTrace } from '@/utils/trace';

import { hubxAnalytic } from './providers/analytics';
import { analytics } from './providers/firebase';
import { gb } from './providers/growthbook';

export const AppConfig = ({ children }: { children: ReactNode }): ReactNode => {
  const { anonymousId, setExperimentDetails } = useExperiment();

  useSingleTrigger(() => {
    logHubxEvent({
      eventName: CommonEventsEnum.CUSTOM_SESSION_START,
    });

    const firstVisit = Cookies.get('first_visit_timestamp');
    if (firstVisit !== undefined) {
      return;
    }

    logHubxEvent({
      eventName: CommonEventsEnum.CUSTOM_FIRST_VISIT,
    });
  });

  useSingleTrigger(() => {
    async function init(): Promise<void> {
      const firstVisit = Cookies.get('first_visit_timestamp');
      const isNewUser =
        firstVisit === undefined ||
        new Date(firstVisit).toDateString() === new Date().toDateString();
      const start = performance.now();
      const cfTrace = await getCfTrace();
      await gb.init({ streaming: true });
      await gb.setAttributes({
        ...gb.getAttributes(),
        id: anonymousId ?? hubxAnalytic.getUUID(),
        country: cfTrace?.loc,
        newUser: isNewUser,
      });
      const end = performance.now();

      logHubxEvent({
        eventName: CommonEventsEnum.INITIALIZATION_COMPLETE,
        event_params: {
          amount: Number((end - start).toFixed(2)),
          source: 'growthbook',
          info: 'success',
        },
      });

      const features = gb.getFeatures();
      Object.entries(features).forEach(([key]) => {
        gb.evalFeature(key);
      });

      gb.setTrackingCallback((experiment, result) => {
        const variation = result.key;

        setUserProperties(analytics, {
          experiment: experiment.key,
          variation,
        });
        setExperimentDetails(experiment.key, variation);
      });
    }

    void init();
  });

  return children;
};
